import React from "react";
import { Logo } from "./Logo";
import { useT } from "../lib/i18n";
import { sanitizeTab, VALID_TABS, throttle } from "../lib/security";

const TAB_TO_ID = {
  enterprise: "tab-empresas-b2b",
  segura: "tab-segura-hub",
  contact: "tab-contacto",
};

export default function Header({ activeTab, onTabChange }) {
  const { t, lang, setLang, availableLanguages } = useT();

  const safeChange = React.useMemo(
    () => throttle((tab) => onTabChange(sanitizeTab(tab)), 250),
    [onTabChange],
  );

  const tabs = [
    { id: "enterprise", label: t("nav.enterprise") },
    { id: "segura", label: t("nav.segura") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <header
      className="sticky top-0 z-50 border-b border-gray-100 ph-glass"
      data-testid="site-header"
    >
      <div className="ph-container flex items-center justify-between h-16 lg:h-20">
        <button
          type="button"
          onClick={() => safeChange("enterprise")}
          className="rounded-md"
          data-testid="logo-home-button"
          aria-label="Ir al inicio"
        >
          <Logo />
        </button>

        <nav
          className="hidden md:flex items-center gap-1 bg-white/60 rounded-full p-1 border border-gray-100"
          aria-label="Principal"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              data-active={activeTab === tab.id}
              onClick={() => safeChange(tab.id)}
              className="ph-tab-pill"
              data-testid={TAB_TO_ID[tab.id]}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <select
            value={lang}
            onChange={(e) => {
              const next = e.target.value;
              if (availableLanguages.includes(next)) setLang(next);
            }}
            className="hidden sm:block text-xs rounded-full border border-gray-200 bg-white/70 px-2 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3a1f9c]"
            aria-label={t("common.languageLabel")}
            data-testid="language-selector"
          >
            {availableLanguages.map((l) => (
              <option key={l} value={l}>
                {l.toUpperCase()}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => safeChange("contact")}
            className="ph-btn-primary text-sm px-5 py-2"
            data-testid="header-contact-cta"
            aria-label={t("nav.contactCta")}
          >
            {t("nav.contactCta")}
          </button>
        </div>
      </div>

      {/* Mobile tabs */}
      <div className="md:hidden border-t border-gray-100">
        <div className="ph-container flex justify-around py-2 gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              data-active={activeTab === tab.id}
              onClick={() => safeChange(tab.id)}
              className="ph-tab-pill flex-1 text-xs whitespace-nowrap"
              data-testid={`mobile-${TAB_TO_ID[tab.id]}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

export { VALID_TABS };
